import { zodResolver } from '@hookform/resolvers/zod';
import {
  ALLOWED_CONSUMER_ENVIRONMENTS,
  CONSUMER_ENVIRONMENT_LABELS,
  createConsumerSchema,
  type CreateConsumerInput,
  type InstanceEnvironment,
  type Product,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CommittentiField } from './CommittentiField';

/** Valore della scelta «prodotto nuovo» nel selettore. */
const NEW_PRODUCT = '__new__';

export interface ConsumerCreateDialogProps {
  open: boolean;
  instanceEnvironment: InstanceEnvironment;
  products: Product[];
  /** Errore dell'API da mostrare senza chiudere il dialogo. */
  error?: string | null;
  busy?: boolean;
  onSubmit: (values: CreateConsumerInput) => void;
  onClose: () => void;
}

/**
 * Registrazione di un consumer: nome, prodotto (esistente o nuovo con prefisso), ambiente fra
 * quelli che l'istanza serve (DEC-006 §4), committenti autorizzati. Prodotto e ambiente non si
 * cambieranno più: il dialogo lo dice prima di creare.
 */
export function ConsumerCreateDialog({
  open,
  instanceEnvironment,
  products,
  error = null,
  busy = false,
  onSubmit,
  onClose,
}: ConsumerCreateDialogProps) {
  const environments = ALLOWED_CONSUMER_ENVIRONMENTS[instanceEnvironment];
  const [productChoice, setProductChoice] = useState<string>(products[0]?.id ?? NEW_PRODUCT);
  const creatingProduct = productChoice === NEW_PRODUCT;
  const { control, handleSubmit, setValue, clearErrors } = useForm<CreateConsumerInput>({
    resolver: zodResolver(createConsumerSchema),
    defaultValues: {
      name: '',
      environment: environments[0],
      authorizedCommittenti: [],
      productId: products[0]?.id,
      newProduct: products[0] ? undefined : { name: '', prefix: '' },
    },
  });

  const chooseProduct = (choice: string) => {
    setProductChoice(choice);
    clearErrors(['productId', 'newProduct']);
    if (choice === NEW_PRODUCT) {
      setValue('productId', undefined);
      setValue('newProduct', { name: '', prefix: '' });
    } else {
      setValue('productId', choice);
      setValue('newProduct', undefined);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="consumer-create-title"
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle id="consumer-create-title">Registra un consumer</DialogTitle>
        <DialogContent>
          <Stack spacing={4} sx={{ pt: 1 }}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Nome"
                  required
                  autoFocus
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ??
                    'Es. «EasyCare · ASL Roma E · produzione»: compare sul ticket come provenienza.'
                  }
                />
              )}
            />
            <Controller
              name="productId"
              control={control}
              render={({ fieldState }) => (
                <TextField
                  select
                  label="Prodotto"
                  value={productChoice}
                  onChange={(event) => chooseProduct(event.target.value)}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ??
                    'Non si cambia dopo la creazione: decide il prefisso del codice ticket.'
                  }
                >
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name} · {product.prefix}
                    </MenuItem>
                  ))}
                  <MenuItem value={NEW_PRODUCT}>Prodotto nuovo…</MenuItem>
                </TextField>
              )}
            />
            {creatingProduct && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                <Controller
                  name="newProduct.name"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="Nome del prodotto"
                      required
                      fullWidth
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="newProduct.prefix"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="Prefisso"
                      required
                      fullWidth
                      error={Boolean(fieldState.error)}
                      helperText={
                        fieldState.error?.message ?? '2-5 lettere, unico: es. EC, ER, CC. Fisso dal primo ticket.'
                      }
                      slotProps={{ htmlInput: { maxLength: 5, style: { textTransform: 'uppercase' } } }}
                    />
                  )}
                />
              </Stack>
            )}
            <Controller
              name="environment"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  select
                  label="Ambiente"
                  error={Boolean(fieldState.error)}
                  helperText={`Questa istanza (${instanceEnvironment}) emette token solo per questi ambienti. Non si cambia dopo la creazione.`}
                >
                  {environments.map((value) => (
                    <MenuItem key={value} value={value}>
                      {CONSUMER_ENVIRONMENT_LABELS[value]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="authorizedCommittenti"
              control={control}
              render={({ field, fieldState }) => (
                <CommittentiField value={field.value} onChange={field.onChange} error={fieldState.error?.message} />
              )}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button type="submit" variant="contained" disabled={busy}>
            Registra
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
